// apps/client/src/pages/admin/dashboard/overview/page.tsx

import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
  UsersIcon,
  UserPlusIcon,
  UserCheckIcon,
  UserMinusIcon,
  MoneyIcon,
  CreditCardIcon,
  DownloadIcon,
} from "@phosphor-icons/react";
import { CardContent, CardRoot, TitleCard, ImageCard } from "../_components/card";
import { DataTable, Column } from "../_components/datatables";
import { BarChartForQuantityByPeriode } from "../_components/chart";
import { SelectRangeDate } from "../_components/date-range-overview";
import { subDays, startOfMonth } from "date-fns";
import { useDashboardMetrics } from "@/client/services/dashboard/dashboard-metrics";
import { usePaymentRevenueByRate } from "@/client/services/payment/payment-stats";
import { useSouscriptions } from "@/client/services/souscription/souscription";
import { useChartDataDashboard } from "@/client/services/metrics/metrics-dashboard";
import { Badge } from "@reactive-resume/ui";
import { ACCOUNTING_MONEY_CONTEXT, cn, formatMoney } from "@reactive-resume/utils";
import { PieChart } from "../_components/pie-chart";
import { useSouscriptionsByRate } from "@/client/services/souscription/souscription-stats";
type Period = "Day" | "Week" | "Month" | "Year";

export const OverviewAdPage = () => {
  const [period, setPeriod] = useState<Period>("Month");
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });

  const { data: souscriptionsByRate, isLoading: pieLoading } = useSouscriptionsByRate(
    dateRange.from,
    dateRange.to,
  );

  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(
    dateRange.from,
    dateRange.to,
  );

  const { data: revenueByRate, isLoading: revenueLoading } = usePaymentRevenueByRate(
    dateRange.from,
    dateRange.to,
  );

  const { souscriptions, loading: souscriptionsLoading } = useSouscriptions(1, null);

  // Graphique des paiements
  const { data: chartData, isPending: chartLoading } = useChartDataDashboard(
    period.toLowerCase(),
    dateRange,
  );

  const maxIndex = useMemo(() => {
    if (!chartData?.datasets?.data || chartData.datasets.data.length === 0) return -1;
    const data = chartData.datasets.data;
    let maxIdx = 0;
    let maxVal = data[0];
    for (let i = 1; i < data.length; i++) {
      if (data[i] > maxVal) {
        maxVal = data[i];
        maxIdx = i;
      }
    }
    return maxIdx;
  }, [chartData]);
  const getMetricValue = (value: any): string => {
    if (metricsLoading) return "...";
    if (value === undefined || value === null) return "0";
    if (typeof value === "number") return value.toString();
    return String(value);
  };

  // Colonnes du tableau des souscriptions
  const souscriptionColumns: Column<any>[] = [
    {
      key: "code",
      header: "Code",
      render: (item) => (
        <span className="font-mono text-xs text-admin-text-secondary">{item.code}</span>
      ),
    },
    {
      key: "user",
      header: "Utilisateur",
      render: (item) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-admin-text-primary">
            {item.user?.name || "—"}
          </span>
          <span className="text-xs text-admin-text-muted">{item.user?.email || "—"}</span>
        </div>
      ),
    },
    {
      key: "rate",
      header: "Offre",
      render: (item) => (
        <Badge className="bg-focus-orange/40 text-xs text-admin-text-primary">
          {item.rate?.name || "—"}
        </Badge>
      ),
    },
    {
      key: "valid_from",
      header: "Début",
      render: (item) =>
        new Date(item.valid_from).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      key: "valid_to",
      header: "Fin",
      render: (item) =>
        new Date(item.valid_to).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      key: "is_active",
      header: "Statut",
      render: (item) => (
        <Badge
          variant={item.is_active ? "success" : "error"}
          className={cn(item.is_active ? "bg-focus-success/40" : "bg-focus-error/40")}
        >
          {item.is_active ? "Actif" : "Expiré"}
        </Badge>
      ),
    },
  ];

  const souscriptionData = souscriptions?.data?.slice(0, 5) ?? [];

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
    const today = new Date();
    let range: { from: Date; to: Date };

    switch (newPeriod) {
      case "Day":
        range = { from: subDays(today, 1), to: today };
        break;
      case "Week":
        range = { from: subDays(today, 7), to: today };
        break;
      case "Month":
        range = { from: startOfMonth(today), to: today };
        break;
      case "Year":
        range = { from: new Date(today.getFullYear(), 0, 1), to: today };
        break;
      default:
        range = { from: subDays(today, 30), to: today };
    }
    setDateRange(range);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-bold text-admin-text-primary">Tableau de bord</h1>
          <p className="mt-1 text-sm text-admin-text-muted">
            Vue d'ensemble de l'activité de la plateforme
          </p>
        </motion.div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center rounded-lg border border-admin-border bg-admin-surface p-1">
            {(["Day", "Week", "Month", "Year"] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => handlePeriodChange(p)}
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium transition-colors",
                  period === p
                    ? "bg-admin-primary text-admin-primary-foreground shadow-sm"
                    : "text-admin-text-secondary hover:text-admin-text-primary",
                )}
              >
                {p}
              </button>
            ))}
          </div>
          <SelectRangeDate
            value={dateRange}
            onChange={(value) => {
              if (value?.from) setDateRange({ from: value.from, to: value.to ?? value.from });
            }}
          />
        </div>
      </div>

      {/* Cartes métriques */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard text="Utilisateurs" number={getMetricValue(metrics?.users.total)} />
            <ImageCard>
              <UsersIcon size={48} weight="duotone" className="text-admin-text-secondary" />
            </ImageCard>
          </CardContent>
          <div className="flex items-center justify-between px-5 pb-5 text-xs">
            {/* <span className="text-admin-text-muted">
              <UserPlusIcon size={12} className="inline mr-1" /> Nouveaux : {getMetricValue(metrics?.users.new)}
            </span> */}
            <span className="text-admin-text-muted">
              <UserCheckIcon size={12} className="mr-1 inline text-admin-success" /> Actifs :{" "}
              {getMetricValue(metrics?.users.active)}
            </span>
            <span className="text-admin-text-muted">
              <UserMinusIcon size={12} className="mr-1 inline text-admin-danger" /> Bannis :{" "}
              {getMetricValue(metrics?.users.banned)}
            </span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard text="Paiements" number={getMetricValue(metrics?.payments.total)} />
            <ImageCard>
              <MoneyIcon size={48} weight="duotone" className="text-admin-text-secondary" />
            </ImageCard>
          </CardContent>
          <div className="flex flex-col items-start justify-start px-5 pb-5 text-xs">
            <span className="text-admin-text-muted">
              CA : {formatMoney(metrics?.payments.amount || 0, ACCOUNTING_MONEY_CONTEXT)}
            </span>
            <span className="text-admin-text-muted">
              Moy. : {formatMoney(metrics?.payments.avg || 0, ACCOUNTING_MONEY_CONTEXT)}
            </span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Souscriptions actives"
              number={getMetricValue(metrics?.souscriptions.active)}
            />
            <ImageCard>
              <CreditCardIcon size={48} weight="duotone" className="text-admin-text-secondary" />
            </ImageCard>
          </CardContent>
          <div className="px-5 pb-5 text-xs text-admin-text-muted">
            Expirées : {getMetricValue(metrics?.souscriptions.expired)}
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard text="Téléchargements" number={getMetricValue(metrics?.downloads.total)} />
            <ImageCard>
              <DownloadIcon size={48} weight="duotone" className="text-admin-text-secondary" />
            </ImageCard>
          </CardContent>
        </CardRoot>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CardRoot className="col-span-1">
          <CardContent className="flex-col items-start">
            <div className="w-full">
              <BarChartForQuantityByPeriode
                key="payment-evolution"
                data={chartLoading ? [] : (chartData?.datasets?.data ?? [])}
                labels={chartLoading ? [] : (chartData?.labels ?? [])}
                title="Tendance de paiements"
                className="h-[300px]"
                activeIndex={maxIndex}
              />
            </div>
          </CardContent>
        </CardRoot>

        {/* <CardRoot className="col-span-1">
          <CardContent className="flex-col items-start">
            <div className="w-full">
              <h4 className="text-sm font-medium text-admin-text-secondary mb-4">
                Répartition des souscriptions par offre
              </h4>
              <div className="space-y-3">
                {revenueLoading ? (
                  <div className="text-center text-admin-text-muted py-8">Chargement...</div>
                ) : revenueByRate?.length ? (
                  revenueByRate.map((item) => (
                    <div key={item.rateId} className="flex items-center gap-3">
                      <div className="w-24 text-sm text-admin-text-secondary truncate">
                        {item.rateName}
                      </div>
                      <div className="flex-1 h-2 bg-admin-surface-elevated rounded-full overflow-hidden">
                        <div
                          className="h-full bg-admin-primary rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min((item.count / (revenueByRate[0]?.count || 1)) * 100, 100)}%`,
                          }}
                        />
                      </div>
                      <div className="text-sm font-medium text-admin-text-primary w-12 text-right">
                        {item.count}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-admin-text-muted py-8">
                    Aucune souscription sur cette période
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </CardRoot> */}

        <CardRoot className="col-span-1">
          <CardContent className="flex-col items-start">
            <div className="w-full">
              <h4 className="mb-4 text-sm font-medium text-admin-text-secondary">
                Répartition des souscriptions par offre
              </h4>
              {pieLoading ? (
                <div className="py-8 text-center text-admin-text-muted">Chargement...</div>
              ) : souscriptionsByRate?.length ? (
                <PieChart
                  labels={souscriptionsByRate.map((item) => item.label)}
                  data={souscriptionsByRate.map((item) => item.value)}
                  className="mx-auto max-w-md"
                  height={280}
                  title="Souscriptions par offre"
                />
              ) : (
                <div className="py-8 text-center text-admin-text-muted">
                  Aucune souscription sur cette période
                </div>
              )}
            </div>
          </CardContent>
        </CardRoot>
      </div>

      {/* Tableau des dernières souscriptions */}
      <CardRoot>
        <CardContent className="flex-col items-start">
          <div className="w-full">
            <DataTable
              title="Dernières souscriptions"
              data={souscriptionData}
              columns={souscriptionColumns}
              pageSize={5}
              loading={souscriptionsLoading}
            />
          </div>
        </CardContent>
      </CardRoot>
    </div>
  );
};
